/**
 * TownPicker — step 3 of the laptop path (URL → PIN → town → begin) in a
 * workshop whose scenarios are teams (towns). Shown once per device after the
 * PIN, when the participant chain lists teams and the device has none stored.
 * Same card as the sign-in chooser so the two read as one flow.
 */
import React from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useTranslation } from '../../hooks/useTranslation.jsx';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  width: 100vw;
  min-height: 100vh;
  background: #000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const Card = styled.div`
  background: linear-gradient(180deg, #000 0%, #111 100%);
  border: 1px solid #222;
  border-radius: 4px;
  padding: 40px 40px 32px;
  width: 100%;
  max-width: ${(p) => (p.$wide ? '960px' : '440px')};
  box-sizing: border-box;
  animation: ${fadeIn} 0.5s ease-out;
`;

const Head = styled.div`
  text-align: center;
  margin-bottom: 28px;
`;

const Kicker = styled.div`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #666;
  margin-bottom: 10px;
`;

const Title = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 8px;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #888;
  margin: 0;
  line-height: 1.5;
`;

/* Twenty towns fit one screen in columns; one column on a phone. */
const Choices = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(13.5rem, 1fr));
  gap: 10px;
`;

const Choice = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 18px;
  background: #0a0a0a;
  border: 1px solid #222;
  border-left: 4px solid ${(p) => p.$color || '#333'};
  border-radius: 4px;
  color: #fff;
  font: inherit;
  font-size: 15px;
  line-height: 1.4;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;

  b { font-weight: 700; }
  span { display: block; font-size: 12.5px; color: #888; margin-top: 2px; }

  &::after {
    content: '→';
    color: #555;
    flex-shrink: 0;
  }

  &:hover, &:focus-visible {
    outline: none;
    background: #111;
    border-color: #3949ab;
    border-left-color: ${(p) => p.$color || '#3949ab'};
  }
`;

/**
 * @param {{ teams: Array<{scenarioId:string, slug:string, name:string, shortName?:string, color?:string|null}>,
 *   instanceTitle?: string, onPick: (team: object) => void }} props
 */
export default function TownPicker({ teams = [], instanceTitle = '', onPick }) {
  const { t } = useTranslation();
  return (
    <Container>
      <Card role="group" aria-label={t('pinLogin.town.title')} $wide={teams.length > 10}>
        <Head>
          {instanceTitle && <Kicker>{instanceTitle}</Kicker>}
          <Title>{t('pinLogin.town.title')}</Title>
          <Subtitle>{t('pinLogin.town.subtitle')}</Subtitle>
        </Head>
        <Choices>
          {teams.map((team) => (
            <Choice key={team.scenarioId} type="button" $color={team.color} onClick={() => onPick(team)}>
              <div>
                <b>{team.shortName || team.name}</b>
                {team.shortName && team.name !== team.shortName && <span>{team.name}</span>}
              </div>
            </Choice>
          ))}
        </Choices>
      </Card>
    </Container>
  );
}
